import type { ObservabilityContext } from '../types/observability.types';

export type RequestContextStore = {
  requestId: string;
  correlationId: string;
  context: Partial<ObservabilityContext>;
};

type RequestContextStorage = {
  run: <T>(store: RequestContextStore, fn: () => T) => T;
  getStore: () => RequestContextStore | undefined;
  enterWith: (store: RequestContextStore) => void;
};

let storage: RequestContextStorage;
if (
  typeof globalThis !== 'undefined' &&
  typeof globalThis.process !== 'undefined' &&
  globalThis.process.versions?.node
) {
  const { AsyncLocalStorage } = require('node:async_hooks') as {
    AsyncLocalStorage: new <T>() => {
      run: (store: T, fn: () => unknown) => unknown;
      getStore: () => T | undefined;
      enterWith: (store: T) => void;
    };
  };
  storage = new AsyncLocalStorage<RequestContextStore>() as RequestContextStorage;
} else {
  storage = {
    run: <T>(_store: RequestContextStore, fn: () => T) => fn(),
    getStore: () => undefined,
    enterWith: () => {},
  };
}

export function runWithRequestContext<T>(store: RequestContextStore, fn: () => T): T {
  return storage.run(store, fn);
}

export function getRequestContext(): RequestContextStore | undefined {
  return storage.getStore();
}

export function updateRequestContext(
  patch: Partial<RequestContextStore>,
): RequestContextStore | undefined {
  const current = storage.getStore();
  if (!current) return undefined;
  const merged: RequestContextStore = {
    ...current,
    ...patch,
    context: {
      ...current.context,
      ...patch.context,
    },
  };
  storage.enterWith(merged);
  return merged;
}
